/* ============================================================
   LIVE MODE — "where would the channel be playing right now?"
   ------------------------------------------------------------
   Treat each channel as a broadcast whose playlist has been looping
   forever since a fixed epoch. Build the channel's DETERMINISTIC
   ads-on queue (same every time, so the computed position matches
   what actually plays), sum item durations into a cycle length, and
   map elapsed-since-epoch (mod cycle) onto a queue item + offset.
   ============================================================ */
import { buildChannelQueue } from './queue'
import { type Portion } from './portions'
import { itemDurationSeconds, frameFor, isProgram } from './itemKinds'
import type { Channel, QueueItem } from './types'

// re-exported so the many callers that import these from './live' keep working; the
// behaviour now lives in the per-kind adapter registry (itemKinds.ts).
export { itemDurationSeconds, frameFor }

/** The broadcast anchor: 13:37 UTC, 11 Sept 2001. */
export const LIVE_EPOCH_MS = Date.parse('2001-09-11T13:37:00Z')

/** Guide zoom levels: visible span + axis tick cadence. Index into this from
 *  TvState.guideZoom; lower index = more zoomed in (smaller span). */
export const GUIDE_ZOOMS: ReadonlyArray<{ spanMs: number; tickMs: number }> = [
  { spanMs: 1 * 60_000, tickMs: 15_000 }, // tight enough for 15-second clips
  { spanMs: 2 * 60_000, tickMs: 30_000 },
  { spanMs: 5 * 60_000, tickMs: 1 * 60_000 },
  { spanMs: 10 * 60_000, tickMs: 2 * 60_000 },
  { spanMs: 20 * 60_000, tickMs: 5 * 60_000 },
  { spanMs: 30 * 60_000, tickMs: 10 * 60_000 },
  { spanMs: 60 * 60_000, tickMs: 15 * 60_000 },
  { spanMs: 120 * 60_000, tickMs: 30 * 60_000 },
  { spanMs: 240 * 60_000, tickMs: 60 * 60_000 },
  { spanMs: 480 * 60_000, tickMs: 60 * 60_000 },
]
export const GUIDE_ZOOM_DEFAULT = 5 // the 30-min view

/** Small deterministic PRNG so a channel's ad selection is stable across builds. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/** The channel's deterministic ad RNG (seeded by channel number). Used by BOTH the live/guide queue
 *  and manual playback so the ad cadence + SPLIT POSITIONS match what the guide shows. */
export function channelRandom(ch: Channel): () => number {
  return mulberry32((ch.number ?? 1) * 0x9e3779b1)
}

/**
 * The channel's broadcast queue: natural order, deterministic ad picks (seeded by
 * channel number). This is BOTH what we schedule against AND what we hand the player,
 * so the live position lines up with playback — which means it MUST honour the same
 * `adBumpersEnabled` the playback queue uses, or the guide shows ad breaks (and the
 * cycle length / "now" position diverges) from what actually plays.
 */
export function buildLiveQueue(ch: Channel, adBumpersEnabled: boolean): QueueItem[] {
  return buildChannelQueue(ch, {
    shuffle: false,
    adBumpersEnabled,
    random: channelRandom(ch),
  })
}

export interface LiveSlot {
  /** queue index of the item airing now */
  index: number
  /** seconds into that item */
  offsetSeconds: number
  /** total loop length in seconds */
  cycleSeconds: number
}

/**
 * Where the infinite loop is at `nowMs`. Returns null if we can't compute a
 * meaningful schedule (no items, or no real video durations — e.g. an
 * un-enriched static fallback), so the caller can no-op gracefully.
 */
export function liveSlot(queue: QueueItem[], nowMs: number): LiveSlot | null {
  if (!queue.length) return null
  const durations = queue.map(itemDurationSeconds)
  const cycle = durations.reduce((a, b) => a + b, 0)
  const programSeconds = queue.reduce((a, q, i) => a + (isProgram(q) ? durations[i] : 0), 0)
  if (cycle <= 0 || programSeconds <= 0) return null

  // An infinite item (e.g. an always-on component program) makes the timeline NON-WRAPPING: there's no
  // cycle to mod against — finite items air once from the epoch, then the infinite item catches every
  // moment after it forever. A fully-finite channel wraps modulo its cycle as before.
  const wrapping = Number.isFinite(cycle)
  let pos = (nowMs - LIVE_EPOCH_MS) / 1000
  if (wrapping) { pos = pos % cycle; if (pos < 0) pos += cycle }
  else pos = Math.max(0, pos) // before the epoch on a non-wrapping line → pinned to the start

  let acc = 0
  for (let i = 0; i < queue.length; i++) {
    const d = durations[i]
    if (pos < acc + d || i === queue.length - 1) {
      return { index: i, offsetSeconds: Math.max(0, pos - acc), cycleSeconds: cycle }
    }
    acc += d
  }
  return { index: queue.length - 1, offsetSeconds: 0, cycleSeconds: cycle }
}

/**
 * Whether a queue can be scheduled as a live broadcast at all — it has items and
 * a positive cycle with real video time. Pure (no clock), so callers can gate the
 * live path without reaching for Date.now() during render.
 */
export function isLiveCapable(queue: QueueItem[]): boolean {
  if (!queue.length) return false
  const durations = queue.map(itemDurationSeconds)
  const cycle = durations.reduce((a, b) => a + b, 0)
  // A channel is live as long as it has at least one PROGRAM with real time — whatever its render kind.
  // For a video channel the programs are videos; for the ad channel they are scenes; either way the
  // broadcast loop schedules them and every client (same deterministic queue + shared LIVE_EPOCH) lands
  // on the same segment at the same second.
  const programSeconds = queue.reduce((a, q, i) => a + (isProgram(q) ? durations[i] : 0), 0)
  return cycle > 0 && programSeconds > 0
}

/** Cumulative scheduled length of items [0, index), in seconds. */
function cycleOffsetSeconds(queue: QueueItem[], index: number): number {
  let acc = 0
  for (let i = 0; i < index && i < queue.length; i++) acc += itemDurationSeconds(queue[i])
  return acc
}

/**
 * How far the player is from the live line, in seconds, as a SIGNED shortest
 * distance around the broadcast loop (negative = behind the broadcast, positive
 * = ahead). Both positions are measured on the same timeline — cumulative item
 * durations + the within-item offset — so a clip boundary is a sub-second step,
 * not a discontinuity. Returns null when there's no usable schedule (liveSlot
 * null) or the player index is out of range. This is the single signal the live
 * condition derives from: |drift| small ⇒ on the broadcast.
 */
export function liveDriftSeconds(
  queue: QueueItem[],
  playerIndex: number,
  playerSeconds: number,
  nowMs: number,
): number | null {
  const slot = liveSlot(queue, nowMs)
  if (!slot || playerIndex < 0 || playerIndex >= queue.length) return null
  const cycle = slot.cycleSeconds
  const playerPos = cycleOffsetSeconds(queue, playerIndex) + playerSeconds
  const livePos = cycleOffsetSeconds(queue, slot.index) + slot.offsetSeconds
  let d = (playerPos - livePos) % cycle
  if (d > cycle / 2) d -= cycle
  else if (d < -cycle / 2) d += cycle
  return d
}

/** Wall-clock ms of the airing of `queueIndex` NEAREST to `nowMs` (signed around the loop: a few
 *  seconds in the past if it just aired, future if upcoming). Used by the guide to scroll the window
 *  so the selected program is always visible. Null if there's no usable schedule. */
export function nearestAiringMs(queue: QueueItem[], queueIndex: number, nowMs: number): number | null {
  const slot = liveSlot(queue, nowMs)
  if (!slot || queueIndex < 0 || queueIndex >= queue.length) return null
  const cycle = slot.cycleSeconds
  const currentPos = cycleOffsetSeconds(queue, slot.index) + slot.offsetSeconds
  const target = cycleOffsetSeconds(queue, queueIndex)
  let delta = (target - currentPos) % cycle
  if (delta > cycle / 2) delta -= cycle
  else if (delta < -cycle / 2) delta += cycle
  return nowMs + delta * 1000
}

/* ============================================================
   DESIRED FRAME — the pure heart of reactive playback.
   ------------------------------------------------------------
   What SHOULD be on screen right now, derived purely from the
   inputs. The reconciler drives the two YouTube buffers toward
   this; transitions/autoplay/live-tracking all fall out of it.
   "Live vs not" is NOT a branch — it's just which offset the
   function returns (liveSlot(now) when live, else manual/0).
   ============================================================ */
export type DesiredFrame =
  | { kind: 'off' }
  // `track`: the offset is a MOVING target to follow continuously (live) — keep
  // drift-correcting to it. False ⇒ a one-shot landing offset (manual nav): seek
  // to it once on arrival, then let the video play out without correction.
  // `atSec` is a CONTENT-second; `portions` carries the item's kept ranges so the
  // executor can convert content↔video at the player boundary (empty = whole video).
  | { kind: 'video'; id: string; atSec: number; index: number; track: boolean; portions: Portion[] }
  | { kind: 'scene'; scene: string; params: Record<string, unknown>; atSec: number; index: number }
  // a React-component segment IS the picture (no video, no 3D bump); `atSec` is the elapsed-since-start
  // the component MAY read, but it runs on the live clock and ignores seeking (seekable: false).
  | { kind: 'component'; component: string; atSec: number; index: number }

export interface DesiredInputs {
  queue: QueueItem[]
  nowMs: number
  live: boolean
  /** explicit non-live position (manual nav); ignored when live. */
  manual: { index: number; atSec: number } | null
  paused: boolean
  noSignal: boolean
  /** Video ids that have failed to play — skipped over so they can't strand us. */
  deadIds?: ReadonlySet<string>
}

/** First index at/after `from` (wrapping) whose item is playable — i.e. not a video
 *  in `deadIds`. Ads are always playable. Returns `from` if everything is dead. */
function nextPlayable(queue: QueueItem[], from: number, deadIds: ReadonlySet<string>): number {
  const n = queue.length
  for (let k = 0; k < n; k++) {
    const i = (((from + k) % n) + n) % n
    const item = queue[i]
    if (item.kind !== 'video' || !deadIds.has(item.videoId)) return i
  }
  return ((from % n) + n) % n
}

/** The single source of truth for "what should be playing right now". */
export function computeDesired(inp: DesiredInputs): DesiredFrame {
  if (inp.noSignal || inp.paused || inp.queue.length === 0) return { kind: 'off' }
  const dead = inp.deadIds ?? EMPTY_DEAD
  if (inp.live) {
    const slot = liveSlot(inp.queue, inp.nowMs)
    if (!slot) return { kind: 'off' }
    // live → follow the moving broadcast line (track = true); if the airing video is
    // dead, jump ahead to the next playable item (we'll re-acquire as the line moves).
    const item = inp.queue[slot.index]
    if (item.kind === 'video' && dead.has(item.videoId)) {
      return frameFor(inp.queue, nextPlayable(inp.queue, slot.index + 1, dead), 0, true)
    }
    return frameFor(inp.queue, slot.index, slot.offsetSeconds, true)
  }
  if (inp.manual) {
    const n = inp.queue.length
    const idx = ((inp.manual.index % n) + n) % n
    // manual → land once at the offset, then play out untracked (track = false). A dead
    // landing skips forward (and restarts at 0, since the offset was for the dead one).
    const item = inp.queue[idx]
    if (item.kind === 'video' && dead.has(item.videoId)) {
      return frameFor(inp.queue, nextPlayable(inp.queue, idx + 1, dead), 0, false)
    }
    return frameFor(inp.queue, idx, inp.manual.atSec, false)
  }
  return { kind: 'off' }
}

const EMPTY_DEAD: ReadonlySet<string> = new Set()

export interface ScheduledItem {
  item: QueueItem
  /** index into the source queue */
  queueIndex: number
  /** absolute wall-clock ms when this airing starts / ends */
  startMs: number
  endMs: number
}

/**
 * The broadcast schedule for a channel across [fromMs, toMs): the sequence of
 * airings (videos + ads) with absolute start/end times, anchored to the same
 * infinite loop as liveSlot. Returns [] when there's no usable schedule
 * (liveSlot null). Walks the queue once from the item airing at fromMs.
 */
export function programsInWindow(queue: QueueItem[], fromMs: number, toMs: number): ScheduledItem[] {
  const slot = liveSlot(queue, fromMs)
  if (!slot || toMs <= fromMs) return []

  const out: ScheduledItem[] = []
  let index = slot.index
  // the item airing at fromMs began offsetSeconds before fromMs
  let startMs = fromMs - slot.offsetSeconds * 1000
  // Tile the loop across the WHOLE window — however many cycles it takes — so the guide shows the
  // broadcast repeating forever (zoom out and future airings keep coming). The only guard we need is
  // against a queue that makes NO time progress: count consecutive zero-duration items and bail after a
  // full lap of them (an all-zero queue), plus an absolute backstop against a pathological tiny window.
  let zeroStreak = 0
  for (let n = 0; n < 100_000 && startMs < toMs; n++) {
    const item = queue[index]
    const durMs = itemDurationSeconds(item) * 1000
    if (durMs > 0) {
      out.push({ item, queueIndex: index, startMs, endMs: startMs + durMs })
      startMs += durMs // an Infinity-duration item ends the walk: startMs → Infinity ≥ toMs
      zeroStreak = 0
    } else if (++zeroStreak > queue.length) {
      break // a whole lap consumed no time → nothing schedulable, stop
    }
    // zero-duration items advance the index without consuming time
    index = (index + 1) % queue.length
  }
  return out
}
